import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Typography, TextField, Grid, Button, CircularProgress, Tooltip, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { EditOutlinedIcon, InfoOutlinedIcon } from 'icons/materialIcons';
import TeamChooseThemeField from 'team/components/editMember/about/TeamChooseThemeField';
import TeamChooseFontField from 'team/components/editMember/about/TeamChooseFontField';
import TeamWhiteButtonWithIcon from 'team/shared/components/button/TeamWhiteButtonWithIcon';
import TeamProfilePhotoCoverPhotoCompanyLogo from 'team/components/editMember/about/profilePhotoCoverPhotoCompanyLogo/TeamProfilePhotoCoverPhotoCompanyLogo';
import TeamCustomLabelEditor from 'team/components/editMember/about/TeamCustomLabelEditor';
import { uploadImageBlob } from 'apis/imageStorageApi';
import { useTeamTemplateContext } from 'team/contexts/TeamTemplateContext';

// CRITICAL: Module-level blob storage to persist blobs across component unmounts
// This ensures blob URLs remain valid even when components re-mount
const templateBlobStorage = new Map(); // Map<templateId, { profilePictureBlob, coverPhotoBlob, companyLogoBlob }>

const TeamTemplateAboutContainer = ({ template, onTemplateUpdate, isCreating = false, onSave, onCancel, isSubmitting = false }) => {
    const {
        templateState,
        updateTemplateState,
        setTemplateSection,
        setTemplateAboutProfilePictureUrl,
        setTemplateAboutCoverPhotoUrl,
        setTemplateAboutCompanyLogoUrl
    } = useTeamTemplateContext();
    const templateId = template?._id || 'new-template';

    // Use ref to track initialization (prevents flickering from premature syncs)
    // Declare this BEFORE it's used in the templateIdRef check below
    const initializedRef = useRef(false);
    const lastInitializedTemplateIdRef = useRef(null);
    const justSavedRef = useRef(false); // Track if we just saved to prevent sync from clearing images

    // Use context state if available (for real-time updates), otherwise use prop
    // Match card pattern: use ref to track template ID and prevent unnecessary recalculations
    const templateIdRef = useRef(templateId);
    if (templateIdRef.current !== templateId) {
        templateIdRef.current = templateId;
        initializedRef.current = false; // Reset initialization when template ID changes
        lastInitializedTemplateIdRef.current = null; // Reset last initialized template ID
    }

    // Get template from state - access directly to avoid unnecessary memoization overhead
    // The key is to only watch specific values in useEffect dependencies, not the whole object
    const currentTemplate = templateState[templateId] || template;

    // Access about directly - we'll use specific values in useEffect dependencies
    const about = currentTemplate?.about || {};

    // State for form fields
    const [templateName, setTemplateName] = useState(currentTemplate?.templateName || '');
    const [company, setCompany] = useState(about.company || '');
    const [location, setLocation] = useState(about.location || '');
    const [bio, setBio] = useState(about.bio || '');

    const [font, setFont] = useState(about.font || 'Inter');
    const [customLinkTypeLabels, setCustomLinkTypeLabels] = useState(about.customLinkTypeLabels || {
        'Contact': 'Contact',
        'Social': 'Social',
        'Products & Services': 'Products & Services'
    });

    // State for image blobs
    const [profilePictureBlob, setProfilePictureBlob] = useState(null);
    const [coverPhotoBlob, setCoverPhotoBlob] = useState(null);
    const [companyLogoBlob, setCompanyLogoBlob] = useState(null);

    // CRITICAL: Store blobs in refs to persist across re-initializations
    // This ensures blob URLs remain valid even when component re-mounts
    const profilePictureBlobRef = useRef(null);
    const coverPhotoBlobRef = useRef(null);
    const companyLogoBlobRef = useRef(null);
    // Initialize image URLs from template (matches card update pattern: cardDetails.card.profilePictureUrl || '')
    // Use null instead of empty string to match card pattern
    const [profilePictureUrl, setProfilePictureUrl] = useState(null);
    const [coverPhotoUrl, setCoverPhotoUrl] = useState(null);
    const [companyLogoUrl, setCompanyLogoUrl] = useState(null);

    // Dialog state for template name
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogTemplateName, setDialogTemplateName] = useState('');

    // ✅ CRITICAL FIX: Initialize form state without overwriting context (matches card pattern lines 566-598)
    // This ensures images are set immediately when template loads from database
    // Re-initialize ONLY when template ID changes, not when template state changes
    useEffect(() => {
        // Get current template from context or prop (recalculated on every render, but we only use it for data, not as dependency)
        const currentTemplate = templateState[templateId] || template;

        console.log('[TeamTemplateAboutContainer] 🔍 INIT useEffect triggered:', {
            templateId,
            hasCurrentTemplate: !!currentTemplate,
            currentTemplateId: currentTemplate?._id,
            isInitialized: initializedRef.current,
            lastInitializedId: lastInitializedTemplateIdRef.current,
        });

        // Initialize if:
        // 1. We have a valid template ID (not 'new-template')
        // 2. We have template data
        // 3. Either we haven't initialized yet OR the template ID has changed (navigated to different template)
        const shouldInitialize = templateId &&
            templateId !== 'new-template' &&
            currentTemplate?._id &&
            (!initializedRef.current || lastInitializedTemplateIdRef.current !== templateId);

        if (shouldInitialize) {
            // CRITICAL: Don't re-initialize if we have active blob URLs (uploads in progress)
            // This prevents clearing blob URLs when uploading multiple images
            const hasActiveBlobUrls =
                (profilePictureUrl && profilePictureUrl.startsWith('blob:')) ||
                (coverPhotoUrl && coverPhotoUrl.startsWith('blob:')) ||
                (companyLogoUrl && companyLogoUrl.startsWith('blob:'));

            if (hasActiveBlobUrls) {
                console.log('[TeamTemplateAboutContainer] ⏭️ INIT skipped: Active blob URLs present (upload in progress)');
                return;
            }
            console.log('[TeamTemplateAboutContainer] ✅ INITIALIZING form state from template:', {
                templateId,
                templateName: currentTemplate.templateName,
                about: currentTemplate.about,
                profilePictureUrl: currentTemplate.about?.profilePictureUrl,
                coverPhotoUrl: currentTemplate.about?.coverPhotoUrl,
                companyLogoUrl: currentTemplate.about?.companyLogoUrl
            });

            // Always sync template name, even if empty (to show default)
            if (currentTemplate.templateName !== undefined) {
                setTemplateName(currentTemplate.templateName);
            }

            const templateAbout = currentTemplate.about || {};

            // Set form fields
            if (templateAbout.company) setCompany(templateAbout.company);
            if (templateAbout.location) setLocation(templateAbout.location);
            if (templateAbout.bio) setBio(templateAbout.bio);

            if (templateAbout.font) setFont(templateAbout.font);
            if (templateAbout.customLinkTypeLabels) setCustomLinkTypeLabels(templateAbout.customLinkTypeLabels);

            // Set image URLs directly from template (matches card pattern line 589-591)
            // CRITICAL: Always set these, even if null, to ensure they're loaded from database
            // BUT: Don't overwrite existing blob URLs (uploads in progress)
            const profileUrl = templateAbout.profilePictureUrl || null;
            const coverUrl = templateAbout.coverPhotoUrl || null;
            const logoUrl = templateAbout.companyLogoUrl || null;

            console.log('[TeamTemplateAboutContainer] ✅ Setting image URLs during init:', {
                profileUrl,
                coverUrl,
                logoUrl,
                currentProfileUrl: profilePictureUrl,
                currentCoverUrl: coverPhotoUrl,
                currentLogoUrl: companyLogoUrl,
                hasBlobInRef: !!companyLogoBlobRef.current,
                hasBlobInState: !!companyLogoBlob
            });

            // CRITICAL: Only set URLs if we don't already have blob URLs (uploads in progress)
            // This prevents overwriting blob URLs that were just set when uploading images
            if (!profilePictureUrl || !profilePictureUrl.startsWith('blob:')) {
                setProfilePictureUrl(profileUrl);
            }
            if (!coverPhotoUrl || !coverPhotoUrl.startsWith('blob:')) {
                setCoverPhotoUrl(coverUrl);
            }
            if (!companyLogoUrl || !companyLogoUrl.startsWith('blob:')) {
                setCompanyLogoUrl(logoUrl);
            }

            // CRITICAL: If we have a blob URL from context, ensure we have the blob in ref
            // The blob must exist in memory for the blob URL to remain valid
            // Restore ALL image blobs from module-level storage if available
            const storedBlobs = templateBlobStorage.get(templateId);

            // Restore profile picture blob
            if (profileUrl && profileUrl.startsWith('blob:')) {
                if (storedBlobs?.profilePictureBlob && !profilePictureBlobRef.current) {
                    console.log('[TeamTemplateAboutContainer] 🔄 Restoring profile blob from module-level storage during init');
                    profilePictureBlobRef.current = storedBlobs.profilePictureBlob;
                    setProfilePictureBlob(storedBlobs.profilePictureBlob);
                    // ⚠️ DON'T create a new blob URL here - it causes infinite loops
                    // The existing blob URL (profileUrl) is still valid as long as the blob is in memory
                    // We just restored the blob to memory, so the URL will work now
                } else if (!profilePictureBlobRef.current && profilePictureBlob) {
                    profilePictureBlobRef.current = profilePictureBlob;
                }
            }

            // Restore cover photo blob
            if (coverUrl && coverUrl.startsWith('blob:')) {
                if (storedBlobs?.coverPhotoBlob && !coverPhotoBlobRef.current) {
                    console.log('[TeamTemplateAboutContainer] 🔄 Restoring cover blob from module-level storage during init');
                    coverPhotoBlobRef.current = storedBlobs.coverPhotoBlob;
                    setCoverPhotoBlob(storedBlobs.coverPhotoBlob);
                    // ⚠️ DON'T create a new blob URL here - it causes infinite loops
                    // The existing blob URL (coverUrl) is still valid as long as the blob is in memory
                    // We just restored the blob to memory, so the URL will work now
                } else if (!coverPhotoBlobRef.current && coverPhotoBlob) {
                    coverPhotoBlobRef.current = coverPhotoBlob;
                }
            }

            // Restore company logo blob
            if (logoUrl && logoUrl.startsWith('blob:')) {
                console.log('[TeamTemplateAboutContainer] 🔍 Checking blob storage during init:', {
                    templateId,
                    hasStorage: templateBlobStorage.has(templateId),
                    hasBlobInStorage: !!storedBlobs?.companyLogoBlob,
                    blobSize: storedBlobs?.companyLogoBlob?.size,
                    allTemplateIds: Array.from(templateBlobStorage.keys())
                });

                if (storedBlobs?.companyLogoBlob && !companyLogoBlobRef.current) {
                    console.log('[TeamTemplateAboutContainer] 🔄 Restoring company logo blob from module-level storage during init');
                    companyLogoBlobRef.current = storedBlobs.companyLogoBlob;
                    setCompanyLogoBlob(storedBlobs.companyLogoBlob);
                    // ⚠️ DON'T create a new blob URL here - it causes infinite loops
                    // The existing blob URL (logoUrl) is still valid as long as the blob is in memory
                    // We just restored the blob to memory, so the URL will work now
                } else if (!companyLogoBlobRef.current && companyLogoBlob) {
                    // Restore blob in ref from state if available
                    console.log('[TeamTemplateAboutContainer] 🔄 Restoring company logo blob in ref from state during init');
                    companyLogoBlobRef.current = companyLogoBlob;
                } else if (!companyLogoBlobRef.current) {
                    // We have a blob URL but no blob - this will cause ERR_FILE_NOT_FOUND
                    // This shouldn't happen, but log it for debugging
                    console.warn('[TeamTemplateAboutContainer] ⚠️ Blob URL exists but no blob in ref, state, or storage:', {
                        logoUrl,
                        templateId,
                        hasBlobInRef: !!companyLogoBlobRef.current,
                        hasBlobInState: !!companyLogoBlob,
                        hasBlobInStorage: !!storedBlobs?.companyLogoBlob,
                        allTemplateIds: Array.from(templateBlobStorage.keys())
                    });
                } else {
                    console.log('[TeamTemplateAboutContainer] ✅ Company logo blob URL and blob ref both exist');
                }
            }

            // Mark as initialized for this template ID
            initializedRef.current = true;
            lastInitializedTemplateIdRef.current = templateId;
            console.log('[TeamTemplateAboutContainer] ✅ Marked as initialized for template:', templateId);
        }
    }, [templateId]); // ⭐ ONLY depend on templateId - prevents re-initialization when uploading images

    // ✅ SYNC LOCAL IMAGE STATE FROM CONTEXT: Update local image state when context changes
    // This ensures that when a template is loaded from server and context is updated, the form fields reflect the changes
    // EXACTLY matches TeamMemberAboutContainer's sync from context (lines 600-633)
    // Use specific image URL values to prevent unnecessary re-runs when other template properties change
    const contextProfileUrl = templateState[templateId]?.about?.profilePictureUrl;
    const contextCoverUrl = templateState[templateId]?.about?.coverPhotoUrl;
    const contextLogoUrl = templateState[templateId]?.about?.companyLogoUrl;

    // Use refs to track previous values and prevent unnecessary updates
    const prevContextProfileUrlRef = useRef(contextProfileUrl);
    const prevContextCoverUrlRef = useRef(contextCoverUrl);
    const prevContextLogoUrlRef = useRef(contextLogoUrl);

    useEffect(() => {
        // Skip if values haven't actually changed (prevents flickering)
        const profileChanged = contextProfileUrl !== prevContextProfileUrlRef.current;
        const coverChanged = contextCoverUrl !== prevContextCoverUrlRef.current;
        const logoChanged = contextLogoUrl !== prevContextLogoUrlRef.current;

        if (!profileChanged && !coverChanged && !logoChanged) {
            return; // No changes, skip sync
        }

        // Update refs immediately to prevent re-triggering
        prevContextProfileUrlRef.current = contextProfileUrl;
        prevContextCoverUrlRef.current = contextCoverUrl;
        prevContextLogoUrlRef.current = contextLogoUrl;

        console.log('[TeamTemplateAboutContainer] 🔍 SYNC useEffect triggered:', {
            templateId,
            isNewTemplate: templateId === 'new-template',
            isInitialized: initializedRef.current,
            profileChanged,
            coverChanged,
            logoChanged,
            contextProfileUrl,
            contextCoverUrl,
            contextLogoUrl,
            currentProfileUrl: profilePictureUrl,
            currentCoverUrl: coverPhotoUrl,
            currentLogoUrl: companyLogoUrl
        });

        if (!templateId || templateId === 'new-template' || !initializedRef.current) {
            console.log('[TeamTemplateAboutContainer] ⏭️ SYNC skipped:', {
                reason: !templateId ? 'no templateId' : templateId === 'new-template' ? 'new template' : 'not initialized'
            });
            return;
        }

        // CRITICAL: Skip sync if we just saved - this prevents clearing images immediately after save
        if (justSavedRef.current) {
            console.log('[TeamTemplateAboutContainer] ⏭️ SYNC skipped (just saved, preventing image clear)');
            return;
        }

        // CRITICAL: Skip sync if we have any active blob URLs (upload in progress)
        // This prevents flickering when uploading images
        const hasActiveBlobUrls =
            (profilePictureUrl && profilePictureUrl.startsWith('blob:')) ||
            (coverPhotoUrl && coverPhotoUrl.startsWith('blob:')) ||
            (companyLogoUrl && companyLogoUrl.startsWith('blob:'));

        if (hasActiveBlobUrls) {
            console.log('[TeamTemplateAboutContainer] ⏭️ SYNC skipped (active blob URLs - upload in progress)');
            return;
        }

        // Sync image URLs from context to local state
        // Only update if the context value is different from local state to avoid unnecessary updates
        // Match the card pattern: check for undefined before syncing
        if (profileChanged && contextProfileUrl !== undefined) {
            const url = contextProfileUrl || null;
            setProfilePictureUrl(prev => {
                // Only update if different to prevent unnecessary re-renders
                // Skip if we have a blob URL (upload in progress) - should be caught above, but double-check
                if (prev && typeof prev === 'string' && prev.startsWith('blob:')) {
                    console.log('[TeamTemplateAboutContainer] ⏭️ Profile URL sync skipped (blob URL in progress)');
                    return prev;
                }
                if (prev !== url) {
                    console.log('[TeamTemplateAboutContainer] 🔄 Updating profile URL:', { from: prev, to: url });
                    return url;
                }
                return prev;
            });
        }

        if (coverChanged && contextCoverUrl !== undefined) {
            const url = contextCoverUrl || null;
            setCoverPhotoUrl(prev => {
                if (prev && typeof prev === 'string' && prev.startsWith('blob:')) {
                    console.log('[TeamTemplateAboutContainer] ⏭️ Cover URL sync skipped (blob URL in progress)');
                    return prev;
                }
                // CRITICAL: Don't overwrite a valid Firebase URL with null/undefined
                if (url && url.trim()) {
                    if (prev !== url) {
                        console.log('[TeamTemplateAboutContainer] 🔄 Updating cover URL from context:', { from: prev, to: url });
                        return url;
                    }
                } else if (!prev || prev === null || prev === '') {
                    if (prev !== url) {
                        console.log('[TeamTemplateAboutContainer] 🔄 Clearing cover URL (both local and context empty)');
                        return url;
                    }
                }
                return prev;
            });
        }

        if (logoChanged && contextLogoUrl !== undefined) {
            const url = contextLogoUrl || null;
            setCompanyLogoUrl(prev => {
                // CRITICAL: Always preserve blob URLs - never overwrite them
                // Blob URLs are for real-time preview and should persist until upload completes
                if (prev && typeof prev === 'string' && prev.startsWith('blob:')) {
                    console.log('[TeamTemplateAboutContainer] ⏭️ Logo URL sync skipped (blob URL in progress)');
                    return prev;
                }
                // CRITICAL: Don't overwrite a valid Firebase URL with null/undefined
                if (url && url.trim()) {
                    if (prev !== url) {
                        console.log('[TeamTemplateAboutContainer] 🔄 Updating logo URL from context:', { from: prev, to: url });
                        return url;
                    }
                } else if (!prev || prev === null || prev === '') {
                    // Only clear if both local and context are empty/null
                    // Don't clear if we have a blob URL (upload in progress)
                    if (prev !== url && !(prev && prev.startsWith('blob:'))) {
                        console.log('[TeamTemplateAboutContainer] 🔄 Clearing logo URL (both local and context empty)');
                        return url;
                    }
                }
                return prev;
            });
        }
    }, [templateId, contextProfileUrl, contextCoverUrl, contextLogoUrl, profilePictureUrl, coverPhotoUrl, companyLogoUrl]); // Include local URLs to check for blob URLs


    const handleChange = useCallback((field, value) => {
        const currentTemplateId = template?._id || 'new-template';

        // Update local state immediately for responsive UI
        // Update context immediately for real-time preview (matches card update pattern)
        const updatedAbout = {
            ...about,
            [field]: value
        };

        // Update context immediately for real-time preview
        setTemplateSection(currentTemplateId, 'about', { [field]: value });
        updateTemplateState(currentTemplateId, {
            about: updatedAbout
        });

        // Also update parent via onTemplateUpdate (for save functionality)
        onTemplateUpdate('about', updatedAbout);
    }, [about, template, onTemplateUpdate, setTemplateSection, updateTemplateState]);

    const handleThemeChange = (newTheme) => {
        handleChange('cardTheme', newTheme);
    };

    const handleLinkColorChange = (newLinkColor) => {
        handleChange('linkColor', newLinkColor);
    };

    const handleFontChange = (newFont) => {
        setFont(newFont);
        handleChange('font', newFont);
    };



    const handleTemplateNameClick = () => {
        setDialogTemplateName(templateName);
        setOpenDialog(true);
    };

    const handleDialogClose = () => {
        setOpenDialog(false);
    };

    const handleTemplateNameChange = () => {
        const currentTemplateId = template?._id || 'new-template';
        setTemplateName(dialogTemplateName);

        // Update context immediately for real-time preview
        updateTemplateState(currentTemplateId, {
            templateName: dialogTemplateName
        });

        // Also update parent via onTemplateUpdate (for save functionality)
        onTemplateUpdate('templateName', dialogTemplateName);
        setOpenDialog(false);
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        switch (name) {
            case 'company':
                setCompany(value);
                handleChange('company', value);
                break;
            case 'location':
                setLocation(value);
                handleChange('location', value);
                break;
            case 'bio':
                setBio(value);
                handleChange('bio', value);
                break;
            default:
                break;
        }
    };

    const handleCustomLabelsChange = (newLabels) => {
        setCustomLinkTypeLabels(newLabels);
        handleChange('customLinkTypeLabels', newLabels);
    };

    // Image upload handlers
    // CRITICAL: Update context immediately with blob URL for real-time preview (matches card update pattern)
    // The blob URL will be replaced with Firebase URL after upload completes
    const handleCroppedProfilePictureBlob = useCallback((blob) => {
        const currentTemplateId = template?._id || 'new-template';

        console.log('[TeamTemplateAboutContainer] 🖼️ handleCroppedProfilePictureBlob called:', {
            templateId: currentTemplateId,
            hasBlob: !!blob,
            isBlob: blob instanceof Blob,
            isString: typeof blob === 'string'
        });

        // CRITICAL: Store blob in state, ref, AND module-level storage
        setProfilePictureBlob(blob);
        profilePictureBlobRef.current = blob;

        // Store in module-level Map
        if (!templateBlobStorage.has(currentTemplateId)) {
            templateBlobStorage.set(currentTemplateId, {});
        }
        templateBlobStorage.get(currentTemplateId).profilePictureBlob = blob;

        // Revoke previous blob URL if it exists
        if (profilePictureUrl && profilePictureUrl.startsWith('blob:')) {
            URL.revokeObjectURL(profilePictureUrl);
        }

        // If it's a URL string (from Team Assets), use it directly
        // Otherwise create a temporary blob URL for preview
        const url = typeof blob === 'string' ? blob : URL.createObjectURL(blob);
        setProfilePictureUrl(url);

        // Update context immediately
        setTemplateAboutProfilePictureUrl(currentTemplateId, url);
        setTemplateSection(currentTemplateId, 'about', { profilePictureUrl: url });
    }, [profilePictureUrl, template, setTemplateAboutProfilePictureUrl, setTemplateSection]);

    const handleCroppedCoverPhotoBlob = useCallback((blob) => {
        const currentTemplateId = template?._id || 'new-template';

        console.log('[TeamTemplateAboutContainer] 🖼️ handleCroppedCoverPhotoBlob called:', {
            templateId: currentTemplateId,
            hasBlob: !!blob,
            isBlob: blob instanceof Blob,
            isString: typeof blob === 'string'
        });

        setCoverPhotoBlob(blob);
        coverPhotoBlobRef.current = blob;

        if (!templateBlobStorage.has(currentTemplateId)) {
            templateBlobStorage.set(currentTemplateId, {});
        }
        templateBlobStorage.get(currentTemplateId).coverPhotoBlob = blob;

        if (coverPhotoUrl && coverPhotoUrl.startsWith('blob:')) {
            URL.revokeObjectURL(coverPhotoUrl);
        }

        const url = typeof blob === 'string' ? blob : URL.createObjectURL(blob);
        setCoverPhotoUrl(url);

        setTemplateAboutCoverPhotoUrl(currentTemplateId, url);
        setTemplateSection(currentTemplateId, 'about', { coverPhotoUrl: url });
    }, [coverPhotoUrl, template, setTemplateAboutCoverPhotoUrl, setTemplateSection]);

    const handleCroppedCompanyLogoBlob = useCallback((blob) => {
        const currentTemplateId = template?._id || 'new-template';

        console.log('[TeamTemplateAboutContainer] 🖼️ handleCroppedCompanyLogoBlob called:', {
            templateId: currentTemplateId,
            hasBlob: !!blob,
            isBlob: blob instanceof Blob,
            isString: typeof blob === 'string'
        });

        setCompanyLogoBlob(blob);
        companyLogoBlobRef.current = blob;

        if (!templateBlobStorage.has(currentTemplateId)) {
            templateBlobStorage.set(currentTemplateId, {});
        }
        templateBlobStorage.get(currentTemplateId).companyLogoBlob = blob;

        if (companyLogoUrl && companyLogoUrl.startsWith('blob:')) {
            URL.revokeObjectURL(companyLogoUrl);
        }

        const url = typeof blob === 'string' ? blob : URL.createObjectURL(blob);
        setCompanyLogoUrl(url);

        setTemplateAboutCompanyLogoUrl(currentTemplateId, url);
        setTemplateSection(currentTemplateId, 'about', { companyLogoUrl: url });
    }, [companyLogoUrl, template, setTemplateAboutCompanyLogoUrl, setTemplateSection]);

    const handleProfilePictureUrlDelete = useCallback(() => {
        // Revoke blob URL if it exists to prevent memory leaks
        if (profilePictureUrl && profilePictureUrl.startsWith('blob:')) {
            URL.revokeObjectURL(profilePictureUrl);
        }
        setProfilePictureBlob(null);
        setProfilePictureUrl('');
        handleChange('profilePictureUrl', '');
    }, [handleChange, profilePictureUrl]);

    const handleCoverPhotoUrlDelete = useCallback(() => {
        // Revoke blob URL if it exists to prevent memory leaks
        if (coverPhotoUrl && coverPhotoUrl.startsWith('blob:')) {
            URL.revokeObjectURL(coverPhotoUrl);
        }
        setCoverPhotoBlob(null);
        setCoverPhotoUrl('');
        handleChange('coverPhotoUrl', '');
    }, [handleChange, coverPhotoUrl]);

    const handleCompanyLogoUrlDelete = useCallback(() => {
        // Revoke blob URL if it exists to prevent memory leaks
        if (companyLogoUrl && companyLogoUrl.startsWith('blob:')) {
            URL.revokeObjectURL(companyLogoUrl);
        }
        setCompanyLogoBlob(null);
        setCompanyLogoUrl('');
        handleChange('companyLogoUrl', '');
    }, [handleChange, companyLogoUrl]);

    // Enhanced save handler that uploads images
    // Uploads images to Firebase Storage and saves URLs to template
    const handleSaveWithImages = useCallback(async () => {
        console.log('[TeamTemplateAboutContainer] handleSaveWithImages called', {
            isSubmitting,
            hasProfileBlob: !!profilePictureBlob,
            hasCoverBlob: !!coverPhotoBlob,
            hasLogoBlob: !!companyLogoBlob,
            templateId: template?._id
        });

        if (isSubmitting) {
            console.log('[TeamTemplateAboutContainer] Already submitting, ignoring click');
            return;
        }

        try {
            // Helper function to check if URL is a blob URL (temporary, won't persist)
            const isBlobUrl = (url) => url && typeof url === 'string' && url.startsWith('blob:');

            // Helper function to restore blob from blob URL if blob is missing
            // This handles the case where blob URL exists but blob object was lost (e.g., after re-render)
            const restoreBlobFromUrl = async (blobUrl, currentBlob, blobRef, setBlob, storageKey) => {
                if (!blobUrl || !isBlobUrl(blobUrl)) {
                    return currentBlob; // Not a blob URL, return existing blob
                }

                // If we already have the blob, return it
                if (currentBlob || blobRef?.current) {
                    console.log(`[TeamTemplateAboutContainer] ✅ ${storageKey} blob already available`);
                    return currentBlob || blobRef?.current;
                }

                // Try to restore blob from module-level storage
                const templateId = template?._id || 'new-template';
                const storedBlobs = templateBlobStorage.get(templateId);
                if (storedBlobs?.[storageKey]) {
                    console.log(`[TeamTemplateAboutContainer] 🔄 Restoring ${storageKey} blob from module storage`);
                    const restoredBlob = storedBlobs[storageKey];
                    setBlob(restoredBlob);
                    blobRef.current = restoredBlob;
                    return restoredBlob;
                }

                // Last resort: try to fetch blob from blob URL
                // NOTE: This may fail if the blob URL has been revoked or is no longer valid
                try {
                    console.log(`[TeamTemplateAboutContainer] 🔄 Attempting to fetch ${storageKey} blob from blob URL:`, blobUrl.substring(0, 50));
                    const response = await fetch(blobUrl);
                    if (!response.ok) {
                        throw new Error(`Failed to fetch blob: ${response.status} ${response.statusText}`);
                    }
                    const blob = await response.blob();
                    if (blob && blob.size > 0) {
                        console.log(`[TeamTemplateAboutContainer] ✅ Successfully restored ${storageKey} blob from URL (size: ${blob.size} bytes)`);
                        setBlob(blob);
                        blobRef.current = blob;
                        // Store in module-level storage for future use
                        if (!templateBlobStorage.has(templateId)) {
                            templateBlobStorage.set(templateId, {});
                        }
                        templateBlobStorage.get(templateId)[storageKey] = blob;
                        return blob;
                    } else {
                        console.warn(`[TeamTemplateAboutContainer] ⚠️ Fetched ${storageKey} blob is empty`);
                    }
                } catch (error) {
                    console.error(`[TeamTemplateAboutContainer] ❌ Failed to restore ${storageKey} blob from URL:`, error);
                    console.error(`[TeamTemplateAboutContainer] This usually means the blob URL has been revoked or is no longer valid.`);
                }

                // If we couldn't restore the blob, return null
                // This will trigger the validation error that prevents saving with a blob URL
                console.warn(`[TeamTemplateAboutContainer] ⚠️ Could not restore ${storageKey} blob - upload will be skipped`);
                return null;
            };

            // Helper function to get valid Firebase URL (not blob URL)
            // CRITICAL: Reject blob URLs - they must be uploaded to Firebase first
            const getValidUrl = (stateUrl, aboutUrl, hasBlob) => {
                // If we have a blob to upload, we'll get the Firebase URL from the upload
                // So for now, use the existing Firebase URL from database (or null for new templates)
                if (hasBlob) {
                    // If aboutUrl is a Firebase URL (not blob), use it as fallback
                    // Otherwise return null (will be set after upload completes)
                    if (aboutUrl && !isBlobUrl(aboutUrl)) {
                        return aboutUrl;
                    }
                    return null; // Will be set after upload
                }

                // If state URL exists and is NOT a blob URL, use it (it's a Firebase URL)
                if (stateUrl && !isBlobUrl(stateUrl)) {
                    return stateUrl;
                }

                // If state URL is a blob URL but we don't have a blob to upload, reject it
                // This means the user selected an image but it wasn't properly uploaded
                if (isBlobUrl(stateUrl)) {
                    console.warn('[TeamTemplateAboutContainer] Rejecting blob URL - no blob to upload:', stateUrl.substring(0, 50));
                    // Use existing Firebase URL from database, or null
                    return (aboutUrl && !isBlobUrl(aboutUrl)) ? aboutUrl : null;
                }

                // Otherwise, use the URL from about (which should be from database/Firebase)
                // Only if it's not a blob URL
                if (aboutUrl && !isBlobUrl(aboutUrl)) {
                    return aboutUrl;
                }
                return null;
            };

            // Upload images if blobs exist
            // For new templates, use 'new-template' as ID (Firebase Storage URLs are permanent, so this works)
            // For existing templates, use the actual template ID
            const templateId = template?._id || 'new-template';

            // CRITICAL: Restore blobs from blob URLs if they're missing
            // This handles the case where blob URL exists but blob object was lost (e.g., after component re-render)
            // This matches how cards handle image uploads - they always check for blobs before saving
            console.log('[TeamTemplateAboutContainer] 🔍 Checking for blobs before upload:', {
                hasProfileBlob: !!profilePictureBlob,
                hasProfileBlobRef: !!profilePictureBlobRef.current,
                hasProfileBlobUrl: !!profilePictureUrl,
                isProfileBlobUrl: isBlobUrl(profilePictureUrl),
                hasCoverBlob: !!coverPhotoBlob,
                hasCoverBlobRef: !!coverPhotoBlobRef.current,
                hasCoverBlobUrl: !!coverPhotoUrl,
                isCoverBlobUrl: isBlobUrl(coverPhotoUrl),
                hasLogoBlob: !!companyLogoBlob,
                hasLogoBlobRef: !!companyLogoBlobRef.current,
                hasLogoBlobUrl: !!companyLogoUrl,
                isLogoBlobUrl: isBlobUrl(companyLogoUrl)
            });

            let restoredProfileBlob = profilePictureBlob || profilePictureBlobRef.current;
            let restoredCoverBlob = coverPhotoBlob || coverPhotoBlobRef.current;
            let restoredLogoBlob = companyLogoBlob || companyLogoBlobRef.current;

            // If we have a blob URL but no blob, try to restore it
            if (profilePictureUrl && isBlobUrl(profilePictureUrl) && !restoredProfileBlob) {
                console.log('[TeamTemplateAboutContainer] 🔄 Attempting to restore profile blob from URL');
                restoredProfileBlob = await restoreBlobFromUrl(profilePictureUrl, profilePictureBlob, profilePictureBlobRef, setProfilePictureBlob, 'profilePictureBlob');
                console.log('[TeamTemplateAboutContainer] Profile blob restoration result:', {
                    success: !!restoredProfileBlob,
                    blobSize: restoredProfileBlob?.size
                });
            }
            if (coverPhotoUrl && isBlobUrl(coverPhotoUrl) && !restoredCoverBlob) {
                console.log('[TeamTemplateAboutContainer] 🔄 Attempting to restore cover blob from URL');
                restoredCoverBlob = await restoreBlobFromUrl(coverPhotoUrl, coverPhotoBlob, coverPhotoBlobRef, setCoverPhotoBlob, 'coverPhotoBlob');
                console.log('[TeamTemplateAboutContainer] Cover blob restoration result:', {
                    success: !!restoredCoverBlob,
                    blobSize: restoredCoverBlob?.size
                });
            }
            if (companyLogoUrl && isBlobUrl(companyLogoUrl) && !restoredLogoBlob) {
                console.log('[TeamTemplateAboutContainer] 🔄 Attempting to restore logo blob from URL');
                restoredLogoBlob = await restoreBlobFromUrl(companyLogoUrl, companyLogoBlob, companyLogoBlobRef, setCompanyLogoBlob, 'companyLogoBlob');
                console.log('[TeamTemplateAboutContainer] Logo blob restoration result:', {
                    success: !!restoredLogoBlob,
                    blobSize: restoredLogoBlob?.size
                });
            }

            console.log('[TeamTemplateAboutContainer] ✅ Final blob status before upload:', {
                hasProfileBlob: !!restoredProfileBlob,
                hasCoverBlob: !!restoredCoverBlob,
                hasLogoBlob: !!restoredLogoBlob,
                profilePictureUrl,
                isProfileBlobUrl: isBlobUrl(profilePictureUrl),
                coverPhotoUrl,
                isCoverBlobUrl: isBlobUrl(coverPhotoUrl),
                companyLogoUrl,
                isLogoBlobUrl: isBlobUrl(companyLogoUrl)
            });

            // CRITICAL: Validate that if we have a blob URL, we must have a blob to upload
            // If we have a blob URL but no blob, the upload will fail - prevent saving
            if (profilePictureUrl && isBlobUrl(profilePictureUrl) && !restoredProfileBlob) {
                throw new Error('Profile picture must be uploaded to Firebase Storage before saving. Please wait for the upload to complete and try again.');
            }
            if (coverPhotoUrl && isBlobUrl(coverPhotoUrl) && !restoredCoverBlob) {
                throw new Error('Cover photo must be uploaded to Firebase Storage before saving. Please wait for the upload to complete and try again.');
            }
            if (companyLogoUrl && isBlobUrl(companyLogoUrl) && !restoredLogoBlob) {
                throw new Error('Company logo must be uploaded to Firebase Storage before saving. Please wait for the upload to complete and try again.');
            }

            // Collect all image URLs - prioritize Firebase URLs from database, not blob URLs
            // Priority: newly uploaded Firebase URL > existing Firebase URL from state > about object URL
            // CRITICAL: Pass hasBlob flag to reject blob URLs when there's no real blob to upload
            // We only upload if restored blob is an actual Blob instance (not a string URL)
            const getHasBlobToUpload = (blob) => blob && blob instanceof Blob;
            
            let finalProfilePictureUrl = getValidUrl(profilePictureUrl, about.profilePictureUrl, getHasBlobToUpload(restoredProfileBlob));
            let finalCoverPhotoUrl = getValidUrl(coverPhotoUrl, about.coverPhotoUrl, getHasBlobToUpload(restoredCoverBlob));
            let finalCompanyLogoUrl = getValidUrl(companyLogoUrl, about.companyLogoUrl, getHasBlobToUpload(restoredLogoBlob));

            // Upload profile picture if REAL blob exists
            if (getHasBlobToUpload(restoredProfileBlob)) {
                try {
                    const uploadedUrl = await uploadImageBlob(
                        restoredProfileBlob,
                        (url) => {
                            // Update local state with Firebase URL (matches card update pattern)
                            setProfilePictureUrl(url);
                        },
                        'template',
                        'profile',
                        templateId
                    );

                    if (!uploadedUrl || typeof uploadedUrl !== 'string' || uploadedUrl.startsWith('blob:')) {
                        throw new Error('Profile picture upload failed - received invalid URL');
                    }

                    finalProfilePictureUrl = uploadedUrl;
                    // Ensure state is updated with Firebase URL (replaces blob URL)
                    setProfilePictureUrl(uploadedUrl);
                    // Update context with Firebase URL (similar to card update pattern)
                    if (setTemplateAboutProfilePictureUrl) {
                        setTemplateAboutProfilePictureUrl(templateId, uploadedUrl);
                    }
                    // Revoke the blob URL now that we have the Firebase URL
                    if (profilePictureUrl && profilePictureUrl.startsWith('blob:')) {
                        URL.revokeObjectURL(profilePictureUrl);
                    }
                    // Clear the blob after successful upload
                    setProfilePictureBlob(null);
                    console.log('[TeamTemplateAboutContainer] ✅ Profile picture uploaded successfully:', uploadedUrl);
                } catch (error) {
                    console.error('[TeamTemplateAboutContainer] ❌ Error uploading profile picture:', error);
                    // Don't save blob URL - use existing Firebase URL or null
                    finalProfilePictureUrl = about.profilePictureUrl || null;
                    throw error; // Re-throw to be caught by outer try-catch
                }
            } else if (restoredProfileBlob && typeof restoredProfileBlob === 'string' && !isBlobUrl(restoredProfileBlob)) {
                // It's already a valid non-blob URL (e.g. from Team Assets)
                finalProfilePictureUrl = restoredProfileBlob;
                console.log('[TeamTemplateAboutContainer] ✅ Using existing Team Asset URL for profile picture:', finalProfilePictureUrl);
            }

            // Upload cover photo if REAL blob exists
            if (getHasBlobToUpload(restoredCoverBlob)) {
                try {
                    const uploadedUrl = await uploadImageBlob(
                        restoredCoverBlob,
                        (url) => {
                            // Update local state with Firebase URL (matches card update pattern)
                            setCoverPhotoUrl(url);
                        },
                        'template',
                        'cover',
                        templateId
                    );

                    if (!uploadedUrl || typeof uploadedUrl !== 'string' || uploadedUrl.startsWith('blob:')) {
                        throw new Error('Cover photo upload failed - received invalid URL');
                    }

                    finalCoverPhotoUrl = uploadedUrl;
                    // Ensure state is updated with Firebase URL (replaces blob URL)
                    setCoverPhotoUrl(uploadedUrl);
                    // Update context with Firebase URL (similar to card update pattern)
                    if (setTemplateAboutCoverPhotoUrl) {
                        setTemplateAboutCoverPhotoUrl(templateId, uploadedUrl);
                    }
                    // Revoke the blob URL now that we have the Firebase URL
                    if (coverPhotoUrl && coverPhotoUrl.startsWith('blob:')) {
                        URL.revokeObjectURL(coverPhotoUrl);
                    }
                    // Clear the blob after successful upload
                    setCoverPhotoBlob(null);
                    console.log('[TeamTemplateAboutContainer] ✅ Cover photo uploaded successfully:', uploadedUrl);
                } catch (error) {
                    console.error('[TeamTemplateAboutContainer] ❌ Error uploading cover photo:', error);
                    // Don't save blob URL - use existing Firebase URL or null
                    finalCoverPhotoUrl = about.coverPhotoUrl || null;
                    throw error; // Re-throw to be caught by outer try-catch
                }
            } else if (restoredCoverBlob && typeof restoredCoverBlob === 'string' && !isBlobUrl(restoredCoverBlob)) {
                // It's already a valid non-blob URL (e.g. from Team Assets)
                finalCoverPhotoUrl = restoredCoverBlob;
                console.log('[TeamTemplateAboutContainer] ✅ Using existing Team Asset URL for cover photo:', finalCoverPhotoUrl);
            }

            // Upload company logo if REAL blob exists
            if (getHasBlobToUpload(restoredLogoBlob)) {
                try {
                    const uploadedUrl = await uploadImageBlob(
                        restoredLogoBlob,
                        (url) => {
                            // Update local state with Firebase URL (matches card update pattern)
                            setCompanyLogoUrl(url);
                        },
                        'template',
                        'logo',
                        templateId
                    );

                    if (!uploadedUrl || typeof uploadedUrl !== 'string' || uploadedUrl.startsWith('blob:')) {
                        throw new Error('Company logo upload failed - received invalid URL');
                    }

                    finalCompanyLogoUrl = uploadedUrl;
                    // Ensure state is updated with Firebase URL (replaces blob URL)
                    setCompanyLogoUrl(uploadedUrl);
                    // Update context with Firebase URL (similar to card update pattern)
                    if (setTemplateAboutCompanyLogoUrl) {
                        setTemplateAboutCompanyLogoUrl(templateId, uploadedUrl);
                    }
                    // Revoke the blob URL now that we have the Firebase URL
                    if (companyLogoUrl && companyLogoUrl.startsWith('blob:')) {
                        URL.revokeObjectURL(companyLogoUrl);
                    }
                    // Clear the blob after successful upload
                    setCompanyLogoBlob(null);
                    console.log('[TeamTemplateAboutContainer] ✅ Company logo uploaded successfully:', uploadedUrl);
                } catch (error) {
                    console.error('[TeamTemplateAboutContainer] ❌ Error uploading company logo:', error);
                    // Don't save blob URL - use existing Firebase URL or null
                    finalCompanyLogoUrl = about.companyLogoUrl || null;
                    throw error; // Re-throw to be caught by outer try-catch
                }
            } else if (restoredLogoBlob && typeof restoredLogoBlob === 'string' && !isBlobUrl(restoredLogoBlob)) {
                // It's already a valid non-blob URL (e.g. from Team Assets)
                finalCompanyLogoUrl = restoredLogoBlob;
                console.log('[TeamTemplateAboutContainer] ✅ Using existing Team Asset URL for company logo:', finalCompanyLogoUrl);
            }

            // CRITICAL: Final validation - ensure no blob URLs are saved
            // If any URL is still a blob URL, reject it and use existing Firebase URL or null
            if (finalProfilePictureUrl && isBlobUrl(finalProfilePictureUrl)) {
                console.error('[TeamTemplateAboutContainer] ⚠️ CRITICAL: Profile picture URL is still a blob URL after upload attempt!', finalProfilePictureUrl);
                finalProfilePictureUrl = about.profilePictureUrl || null;
            }
            if (finalCoverPhotoUrl && isBlobUrl(finalCoverPhotoUrl)) {
                console.error('[TeamTemplateAboutContainer] ⚠️ CRITICAL: Cover photo URL is still a blob URL after upload attempt!', finalCoverPhotoUrl);
                finalCoverPhotoUrl = about.coverPhotoUrl || null;
            }
            if (finalCompanyLogoUrl && isBlobUrl(finalCompanyLogoUrl)) {
                console.error('[TeamTemplateAboutContainer] ⚠️ CRITICAL: Company logo URL is still a blob URL after upload attempt!', finalCompanyLogoUrl);
                finalCompanyLogoUrl = about.companyLogoUrl || null;
            }

            // Update template state with all image URLs (both existing and newly uploaded)
            // This ensures the template state has the correct URLs before saving
            // CRITICAL: Only include URLs that are valid Firebase URLs (not blob URLs, not empty strings)
            // Use null for empty/invalid, but preserve valid Firebase URLs
            // Exclude fullName and jobTitle as templates don't have these fields
            const { fullName, jobTitle, ...aboutWithoutNameAndTitle } = about;
            const updatedAbout = {
                ...aboutWithoutNameAndTitle,
                // Only set if we have a valid Firebase URL (not blob, not empty)
                profilePictureUrl: (finalProfilePictureUrl &&
                    typeof finalProfilePictureUrl === 'string' &&
                    !finalProfilePictureUrl.startsWith('blob:') &&
                    finalProfilePictureUrl.trim()) ? finalProfilePictureUrl : null,
                coverPhotoUrl: (finalCoverPhotoUrl &&
                    typeof finalCoverPhotoUrl === 'string' &&
                    !finalCoverPhotoUrl.startsWith('blob:') &&
                    finalCoverPhotoUrl.trim()) ? finalCoverPhotoUrl : null,
                companyLogoUrl: (finalCompanyLogoUrl &&
                    typeof finalCompanyLogoUrl === 'string' &&
                    !finalCompanyLogoUrl.startsWith('blob:') &&
                    finalCompanyLogoUrl.trim()) ? finalCompanyLogoUrl : null
            };

            console.log('[TeamTemplateAboutContainer] 🔍 Final image URLs for updatedAbout:', {
                profilePictureUrl: updatedAbout.profilePictureUrl,
                coverPhotoUrl: updatedAbout.coverPhotoUrl,
                companyLogoUrl: updatedAbout.companyLogoUrl,
                profileIsValid: updatedAbout.profilePictureUrl && !updatedAbout.profilePictureUrl.startsWith('blob:'),
                coverIsValid: updatedAbout.coverPhotoUrl && !updatedAbout.coverPhotoUrl.startsWith('blob:'),
                logoIsValid: updatedAbout.companyLogoUrl && !updatedAbout.companyLogoUrl.startsWith('blob:')
            });

            // Update parent template state synchronously via context
            // The parent's handleSave will read from context, so we can call onSave immediately
            console.log('[TeamTemplateAboutContainer] Updating template state with about:', updatedAbout);
            console.log('[TeamTemplateAboutContainer] Image URLs being saved:', {
                profilePictureUrl: updatedAbout.profilePictureUrl,
                coverPhotoUrl: updatedAbout.coverPhotoUrl,
                companyLogoUrl: updatedAbout.companyLogoUrl,
                profilePictureUrlType: typeof updatedAbout.profilePictureUrl,
                coverPhotoUrlType: typeof updatedAbout.coverPhotoUrl,
                companyLogoUrlType: typeof updatedAbout.companyLogoUrl
            });

            // CRITICAL: Update template state FIRST via onTemplateUpdate
            // This updates the template prop that handleSave will read from
            console.log('[TeamTemplateAboutContainer] 📤 Calling onTemplateUpdate with updatedAbout:', {
                profilePictureUrl: updatedAbout.profilePictureUrl,
                coverPhotoUrl: updatedAbout.coverPhotoUrl,
                companyLogoUrl: updatedAbout.companyLogoUrl
            });
            onTemplateUpdate('about', updatedAbout);

            // Also directly update the context state to ensure it's immediately available
            // This is a backup to ensure the state is updated even if onTemplateUpdate has delays
            const currentTemplateId = template?._id || 'new-template';

            // CRITICAL: Update context with the complete about object including image URLs
            // Use setTemplateSection to ensure the about section is properly updated
            // IMPORTANT: Use the updatedAbout object which has the Firebase URLs, not the old about
            setTemplateSection(currentTemplateId, 'about', updatedAbout);
            updateTemplateState(currentTemplateId, {
                about: updatedAbout
            });

            // Wait a bit longer to ensure state updates are fully propagated
            // This is critical because the parent's handleSave reads from both template prop and context
            await new Promise(resolve => setTimeout(resolve, 500));

            // CRITICAL: Re-read template from props to verify it was updated
            // The template prop should now have the Firebase URLs from onTemplateUpdate
            console.log('[TeamTemplateAboutContainer] 🔍 After onTemplateUpdate - checking template prop:', {
                templateProfileUrl: template?.about?.profilePictureUrl,
                templateCoverUrl: template?.about?.coverPhotoUrl,
                templateLogoUrl: template?.about?.companyLogoUrl,
                updatedAboutProfileUrl: updatedAbout.profilePictureUrl,
                updatedAboutCoverUrl: updatedAbout.coverPhotoUrl,
                updatedAboutLogoUrl: updatedAbout.companyLogoUrl
            });

            // Verify the context was updated before saving
            const verifyTemplate = templateState[currentTemplateId];
            console.log('[TeamTemplateAboutContainer] Verifying context state before save:', {
                hasTemplate: !!verifyTemplate,
                aboutProfileUrl: verifyTemplate?.about?.profilePictureUrl,
                aboutCoverUrl: verifyTemplate?.about?.coverPhotoUrl,
                aboutLogoUrl: verifyTemplate?.about?.companyLogoUrl,
                // Also check if the URLs are Firebase URLs (not blob URLs)
                profileIsFirebase: verifyTemplate?.about?.profilePictureUrl && !verifyTemplate?.about?.profilePictureUrl.startsWith('blob:'),
                coverIsFirebase: verifyTemplate?.about?.coverPhotoUrl && !verifyTemplate?.about?.coverPhotoUrl.startsWith('blob:'),
                logoIsFirebase: verifyTemplate?.about?.companyLogoUrl && !verifyTemplate?.about?.companyLogoUrl.startsWith('blob:'),
                // Also verify the final URLs we're about to save
                finalProfileUrl: finalProfilePictureUrl,
                finalCoverUrl: finalCoverPhotoUrl,
                finalLogoUrl: finalCompanyLogoUrl
            });

            // CRITICAL: If context doesn't have the URLs, force update it one more time
            // This ensures the URLs are definitely in context before save
            if (!verifyTemplate?.about?.profilePictureUrl && finalProfilePictureUrl && !finalProfilePictureUrl.startsWith('blob:')) {
                console.warn('[TeamTemplateAboutContainer] ⚠️ Profile URL not in context, forcing update...');
                setTemplateSection(currentTemplateId, 'about', { profilePictureUrl: finalProfilePictureUrl });
                updateTemplateState(currentTemplateId, {
                    about: { ...verifyTemplate?.about, profilePictureUrl: finalProfilePictureUrl }
                });
            }
            if (!verifyTemplate?.about?.coverPhotoUrl && finalCoverPhotoUrl && !finalCoverPhotoUrl.startsWith('blob:')) {
                console.warn('[TeamTemplateAboutContainer] ⚠️ Cover URL not in context, forcing update...');
                setTemplateSection(currentTemplateId, 'about', { coverPhotoUrl: finalCoverPhotoUrl });
                updateTemplateState(currentTemplateId, {
                    about: { ...verifyTemplate?.about, coverPhotoUrl: finalCoverPhotoUrl }
                });
            }
            if (!verifyTemplate?.about?.companyLogoUrl && finalCompanyLogoUrl && !finalCompanyLogoUrl.startsWith('blob:')) {
                console.warn('[TeamTemplateAboutContainer] ⚠️ Logo URL not in context, forcing update...');
                setTemplateSection(currentTemplateId, 'about', { companyLogoUrl: finalCompanyLogoUrl });
                updateTemplateState(currentTemplateId, {
                    about: { ...verifyTemplate?.about, companyLogoUrl: finalCompanyLogoUrl }
                });
            }

            // Wait one more time to ensure forced updates are propagated
            await new Promise(resolve => setTimeout(resolve, 200));

            // Call save - pass the Firebase URLs directly to ensure they're used
            // This is critical because React state updates are async, so the template prop
            // might not have the Firebase URLs yet when handleSave runs
            if (onSave) {
                const imageUrlsToSave = {
                    profilePictureUrl: finalProfilePictureUrl,
                    coverPhotoUrl: finalCoverPhotoUrl,
                    companyLogoUrl: finalCompanyLogoUrl
                };

                console.log('[TeamTemplateAboutContainer] 📤 Calling onSave with image URLs:', {
                    profile: finalProfilePictureUrl,
                    cover: finalCoverPhotoUrl,
                    logo: finalCompanyLogoUrl,
                    profileIsFirebase: finalProfilePictureUrl && !finalProfilePictureUrl.startsWith('blob:'),
                    coverIsFirebase: finalCoverPhotoUrl && !finalCoverPhotoUrl.startsWith('blob:'),
                    logoIsFirebase: finalCompanyLogoUrl && !finalCompanyLogoUrl.startsWith('blob:')
                });

                // Mark that we're about to save - this prevents sync useEffect from clearing images
                justSavedRef.current = true;

                // CRITICAL: Pass image URLs directly to handleSave
                // This ensures handleSave uses the Firebase URLs even if state hasn't updated
                await onSave(imageUrlsToSave);
                console.log('[TeamTemplateAboutContainer] onSave completed');

                // CRITICAL FIX: After save, ensure our local state still has the Firebase URLs
                // The template will reload from database, but we want to keep the preview visible
                // by ensuring state has the Firebase URLs we just uploaded (not blob URLs)
                // Wait a bit for the save response to come back with the updated template
                await new Promise(resolve => setTimeout(resolve, 200));

                // Re-read from context after save to get the latest URLs from database
                const savedTemplate = templateState[currentTemplateId];
                const savedProfileUrl = savedTemplate?.about?.profilePictureUrl || finalProfilePictureUrl;
                const savedCoverUrl = savedTemplate?.about?.coverPhotoUrl || finalCoverPhotoUrl;
                const savedLogoUrl = savedTemplate?.about?.companyLogoUrl || finalCompanyLogoUrl;

                console.log('[TeamTemplateAboutContainer] After save - setting image URLs:', {
                    profileUrl: savedProfileUrl,
                    coverUrl: savedCoverUrl,
                    logoUrl: savedLogoUrl,
                    fromContext: !!savedTemplate?.about?.profilePictureUrl
                });

                // Set the URLs from saved template (database) or fall back to what we uploaded
                if (savedProfileUrl && !savedProfileUrl.startsWith('blob:') && savedProfileUrl.trim()) {
                    console.log('[TeamTemplateAboutContainer] Maintaining profile picture URL in state:', savedProfileUrl);
                    setProfilePictureUrl(savedProfileUrl);
                }
                if (savedCoverUrl && !savedCoverUrl.startsWith('blob:') && savedCoverUrl.trim()) {
                    console.log('[TeamTemplateAboutContainer] Maintaining cover photo URL in state:', savedCoverUrl);
                    setCoverPhotoUrl(savedCoverUrl);
                }
                if (savedLogoUrl && !savedLogoUrl.startsWith('blob:') && savedLogoUrl.trim()) {
                    console.log('[TeamTemplateAboutContainer] Maintaining company logo URL in state:', savedLogoUrl);
                    setCompanyLogoUrl(savedLogoUrl);
                }

                // Clear the justSaved flag after a delay to allow sync to work normally again
                setTimeout(() => {
                    justSavedRef.current = false;
                    console.log('[TeamTemplateAboutContainer] Cleared justSaved flag');
                }, 1000);
            } else {
                console.error('[TeamTemplateAboutContainer] onSave is not defined!');
            }
        } catch (error) {
            console.error('[TeamTemplateAboutContainer] Error uploading images:', error);
            console.error('[TeamTemplateAboutContainer] Error stack:', error.stack);
            // Still call save even if image upload fails
            if (onSave) {
                try {
                    console.log('[TeamTemplateAboutContainer] Attempting to save despite image upload error...');
                    await onSave();
                } catch (saveError) {
                    console.error('[TeamTemplateAboutContainer] Error saving template:', saveError);
                    console.error('[TeamTemplateAboutContainer] Save error stack:', saveError.stack);
                }
            }
        }
    }, [profilePictureBlob, coverPhotoBlob, companyLogoBlob, template?._id, onSave, profilePictureUrl, coverPhotoUrl, companyLogoUrl, about, onTemplateUpdate]);


    const defaultTabLabels = [
        { key: 'Contact', label: 'Contact' },
        { key: 'Social', label: 'Social' },
        { key: 'Products & Services', label: 'Products & Services' }
    ];

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                padding: 2,
                backgroundColor: '#ffffff'
            }}
        >
            {/* Header Section */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    marginBottom: 2,
                }}
            >
                <Typography variant="body2" sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#4f4f4f', marginBottom: '10px' }} gutterBottom align="left">
                    About
                </Typography>
            </Box>

            {/* Content Section */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: 2,
                    borderRadius: 1,
                }}
            >
                {/* Form Fields Container */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 2,
                    }}
                >
                    {/* Left-aligned Template Name */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600, color: "#4f4f4f" }}>
                            Template Name:
                        </Typography>
                        <Button
                            variant="text"
                            onClick={handleTemplateNameClick}
                            value={templateName}
                            sx={{
                                textTransform: 'none',
                                padding: '4px 8px',
                                width: "150px",
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                backgroundColor: "#f7f7f7",
                                color: "#212121",
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                textAlign: 'left',
                            }}
                            endIcon={<EditOutlinedIcon sx={{ color: '#828282', fontSize: "14px" }} />}
                        >
                            {templateName || 'Template Name'}
                        </Button>
                    </Box>


                </Box>

                {/* Info section for template context */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        marginTop: 2,
                        marginBottom: 2,
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: '0.75rem',
                            color: '#4f4f4f',
                            lineHeight: 1.5,
                        }}
                    >
                        Any field or image left empty below can be set and edited by the individual profile
                    </Typography>
                    <Tooltip
                        title={
                            <Box>
                                <Typography variant="body2" sx={{ marginBottom: 1, fontSize: '0.75rem' }}>
                                    Any fields or images left empty can be edited on the cards assigned to the template. Fields or images coming from the template will be locked on the assigned cards.
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                    For example, if you set a company logo individuals will NOT be able to override it. However not setting a company logo will allow anyone assigned to this template to upload their own.
                                </Typography>
                            </Box>
                        }
                        arrow
                        placement="top"
                    >
                        <InfoOutlinedIcon sx={{ fontSize: '18px', color: '#828282', flexShrink: 0, cursor: 'help' }} />
                    </Tooltip>
                </Box>

                <TeamProfilePhotoCoverPhotoCompanyLogo
                    onCroppedProfilePictureBlob={handleCroppedProfilePictureBlob}
                    onCroppedCoverPhotoBlob={handleCroppedCoverPhotoBlob}
                    onCroppedCompanyLogoBlob={handleCroppedCompanyLogoBlob}
                    onDeleteProfilePicture={handleProfilePictureUrlDelete}
                    onDeleteCoverPhoto={handleCoverPhotoUrlDelete}
                    onDeleteCompanylogo={handleCompanyLogoUrlDelete}

                    cardDesign="design5"
                    isProfilePictureRestricted={false}
                    isCompanyLogoRestricted={false}
                    canRequestChange={false}
                    onRestrictionNotification={() => { }}
                    initialProfilePictureUrl={profilePictureUrl}
                    initialCoverPhotoUrl={coverPhotoUrl}
                    initialCompanyLogoUrl={companyLogoUrl}
                />
                {/* Debug: Log prop values */}
                {(() => {
                    console.log('[TeamTemplateAboutContainer] 🔍 Props passed to TeamProfilePhotoCoverPhotoCompanyLogo:', {
                        initialProfilePictureUrl: profilePictureUrl,
                        initialCoverPhotoUrl: coverPhotoUrl,
                        initialCompanyLogoUrl: companyLogoUrl
                    });
                    return null;
                })()}

                {/* Location, Company, and Bio Inputs */}
                <Grid container spacing={2} sx={{ marginTop: 4 }}>
                    <Grid item xs={6}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginBottom: "10px" }}>
                            Location
                        </Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Location"
                            name="location"
                            value={location}
                            onChange={handleInputChange}
                            sx={{
                                backgroundColor: '#f7f7f7',
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        border: 'none',
                                    },
                                    '&:hover fieldset': {
                                        border: 'none',
                                    },
                                    '&.Mui-focused fieldset': {
                                        border: 'none',
                                    },
                                    '& input::placeholder': {
                                        fontSize: '0.8rem',
                                        fontWeight: '300',
                                    },
                                },
                            }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginBottom: "10px", marginTop: "5px" }}>
                            Company
                        </Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Company"
                            name="company"
                            value={company}
                            onChange={handleInputChange}
                            sx={{
                                backgroundColor: '#f7f7f7',
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        border: 'none',
                                    },
                                    '&:hover fieldset': {
                                        border: 'none',
                                    },
                                    '&.Mui-focused fieldset': {
                                        border: 'none',
                                    },
                                    '& input::placeholder': {
                                        fontSize: '0.8rem',
                                        fontWeight: '300',
                                    },
                                },
                            }}
                        />
                    </Grid>
                </Grid>

                {/* Bio Field */}
                <Box sx={{ marginTop: 3 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginBottom: "10px" }}>
                        Bio
                    </Typography>
                    <TextField
                        fullWidth
                        name="bio"
                        value={bio}
                        onChange={handleInputChange}
                        multiline
                        minRows={2}
                        maxRows={8}
                        variant="outlined"
                        inputProps={{
                            maxLength: 250
                        }}
                        sx={{
                            backgroundColor: '#f7f7f7',
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    border: 'none',
                                },
                                '&:hover fieldset': {
                                    border: 'none',
                                },
                                '&.Mui-focused fieldset': {
                                    border: 'none',
                                },
                                '& input::placeholder': {
                                    fontSize: '0.8rem',
                                    fontWeight: '300',
                                },
                            },
                        }}
                    />
                    {/* Character Counter */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        mt: 1
                    }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: bio.length >= 250 ? '#1976d2' : '#828282',
                                fontSize: '0.75rem',
                                fontWeight: bio.length >= 250 ? 600 : 400
                            }}
                        >
                            {bio.length}/250 characters
                        </Typography>
                    </Box>
                </Box>

                <TeamChooseThemeField
                    theme={about.cardTheme || '#FF7A00'}
                    linkColor={about.linkColor || '#FF7A00'}
                    onThemeChange={handleThemeChange}
                    onLinkColorChange={handleLinkColorChange}
                    isCardColorRestricted={false}
                    canRequestChange={false}
                />

                {/* Font Selection Field */}
                <Box sx={{ mt: 3 }}>
                    <TeamChooseFontField
                        selectedFont={font}
                        onFontChange={handleFontChange}
                    />
                </Box>

                {/* CustomLabelEditor for custom labels */}
                <Box sx={{ mt: 3 }}>
                    <TeamCustomLabelEditor
                        labels={customLinkTypeLabels}
                        onChange={handleCustomLabelsChange}
                        defaultLabels={defaultTabLabels}
                        loading={false}
                    />
                </Box>
            </Box>

            {/* Footer Section with Cancel and Update buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, marginTop: 'auto', paddingTop: 2 }}>
                <TeamWhiteButtonWithIcon
                    text="Cancel"
                    onClick={onCancel}
                    disabled={isSubmitting}
                    padding="5px 16px"
                    sx={{
                        border: '0.75px solid #dedede',
                        borderRadius: '10px',
                        fontSize: '0.8rem',
                        marginRight: "4px",
                        '&:hover': {
                            backgroundColor: '#f7f7f7',
                            border: '0.75px solid #dedede',
                            boxShadow: 'none',
                        },
                    }}
                />
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleSaveWithImages}
                    disabled={isSubmitting}
                    sx={{
                        backgroundColor: '#000000',
                        padding: '5px 32px',
                        color: '#ffffff',
                        fontWeight: "bold",
                        textTransform: 'none',
                        borderRadius: '10px',
                        '&:hover': {
                            backgroundColor: '#333333',
                        },
                        '&:disabled': {
                            backgroundColor: '#ccc',
                            color: '#fff'
                        }
                    }}
                >
                    {isSubmitting ? <CircularProgress size={24} color="inherit" /> : (isCreating ? 'Save' : 'Update')}
                </Button>
            </Box>

            {/* Dialog for entering the template name */}
            <Dialog open={openDialog} onClose={handleDialogClose}>
                <DialogTitle>Enter Template Name</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Template Name"
                        type="text"
                        fullWidth
                        variant="outlined"
                        name="templateName"
                        placeholder='Template Name'
                        value={dialogTemplateName}
                        onChange={(e) => setDialogTemplateName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleTemplateNameChange} color="primary" variant="contained">
                        Update
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TeamTemplateAboutContainer;
